import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';

/**
 * Compute time until next Monday 08:00 CET.
 * Pure function — no side effects.
 */
const getNextWeeklyTriggerCountdown = (nowMs: number) => {
  const now = new Date(nowMs);
  const currentUtcDay = now.getUTCDay();
  const currentUtcHours = now.getUTCHours();
  const currentUtcMinutes = now.getUTCMinutes();
  const daysUntilMonday = (8 - (currentUtcDay || 7)) % 7;

  const nextTrigger = new Date(now);
  nextTrigger.setUTCDate(now.getUTCDate() + daysUntilMonday);
  nextTrigger.setUTCHours(7, 0, 0, 0); // 08:00 CET == 07:00 UTC

  const isPastThisWeeksTrigger =
    daysUntilMonday === 0 &&
    (currentUtcHours > 7 || (currentUtcHours === 7 && currentUtcMinutes >= 0));

  if (isPastThisWeeksTrigger) {
    nextTrigger.setUTCDate(nextTrigger.getUTCDate() + 7);
  }

  const diffMs = Math.max(0, nextTrigger.getTime() - nowMs);
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  const seconds = Math.floor((diffMs % 60000) / 1000);

  return { days, hours, minutes, seconds };
};

/**
 * Compute countdown to an arbitrary target date string.
 */
const getCountdownToDate = (nowMs: number, targetDateStr: string) => {
  const targetMs = new Date(targetDateStr).getTime();
  const diffMs = Math.max(0, targetMs - nowMs);
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  const seconds = Math.floor((diffMs % 60000) / 1000);
  return { days, hours, minutes, seconds };
};

type CountdownParts = { days: number; hours: number; minutes: number; seconds: number };

interface WeeklyCountdownProps {
  variant: 'detail' | 'grid';
  loading?: boolean;
  /** Campaign start date (ISO string). If in the future, shows countdown to start instead of weekly reset. */
  startDate?: string | null;
  /** Campaign end date (ISO string). If in the past, shows "Campaign Ended" instead of countdown. */
  endDate?: string | null;
}

/**
 * Determines the display mode based on campaign dates relative to now.
 */
const getCountdownMode = (
  startDate?: string | null,
  endDate?: string | null,
): { mode: 'pre_start' | 'ended' | 'weekly'; targetDate?: string } => {
  const nowMs = Date.now();

  if (endDate) {
    const endMs = new Date(endDate).getTime();
    if (endMs <= nowMs) {
      return { mode: 'ended' };
    }
  }

  if (startDate) {
    const startMs = new Date(startDate).getTime();
    if (startMs > nowMs) {
      return { mode: 'pre_start', targetDate: startDate };
    }
  }

  return { mode: 'weekly' };
};

/**
 * Self-contained countdown component.
 *
 * - If `startDate` is in the future: shows countdown to campaign start ("Starts In")
 * - If `endDate` is in the past: shows "Campaign Ended" banner
 * - Otherwise: shows countdown to next Monday 08:00 CET ("Weekly Reset")
 *
 * Has its own useState + setInterval so the 1-second tick only
 * re-renders THIS tiny component — NOT the parent.
 */
export const WeeklyCountdown = ({ variant, loading, startDate, endDate }: WeeklyCountdownProps) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, []);

  const { mode, targetDate } = getCountdownMode(startDate, endDate);

  // Campaign ended
  if (mode === 'ended') {
    if (variant === 'detail') {
      return (
        <div className="inline-flex items-center gap-2 rounded-[12px] border border-amber-400/20 bg-amber-400/8 px-3 py-2.5 text-[13px] font-bold text-amber-200">
          <svg className="h-4 w-4 shrink-0 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z" clipRule="evenodd" />
          </svg>
          Campaign Ended
        </div>
      );
    }
    return (
      <div className="inline-flex items-center gap-1.5 rounded-xl border border-amber-400/15 bg-amber-400/6 px-2 py-1.5 text-[11px] font-bold text-amber-200/80">
        Ended
      </div>
    );
  }

  let parts: CountdownParts;
  if (mode === 'pre_start' && targetDate) {
    parts = getCountdownToDate(now, targetDate);
  } else {
    parts = getNextWeeklyTriggerCountdown(now);
  }

  if (variant === 'detail') {
    if (loading) {
      return (
        <div className="inline-flex items-center gap-1.5 rounded-[12px] border border-white/8 bg-black/25 px-3 py-2.5 text-[15px] font-black tabular-nums text-white">
          <span className="rounded-md bg-white/[0.03] px-2.5 py-1">0d</span>
          <span className="rounded-md bg-white/[0.03] px-2.5 py-1">00</span>
          <span className="text-white/40">:</span>
          <span className="rounded-md bg-white/[0.03] px-2.5 py-1">00</span>
          <span className="text-white/40">:</span>
          <span className="rounded-md bg-white/[0.03] px-2.5 py-1">00</span>
        </div>
      );
    }
    return (
      <div className="inline-flex items-center gap-1.5 rounded-[12px] border border-white/8 bg-black/25 px-3 py-2.5 text-[15px] font-black tabular-nums text-white">
        <span className="rounded-md bg-white/[0.06] px-2.5 py-1">{parts.days}d</span>
        <span className="rounded-md bg-white/[0.06] px-2.5 py-1">{String(parts.hours).padStart(2, '0')}</span>
        <span className="text-white/40">:</span>
        <span className="rounded-md bg-white/[0.06] px-2.5 py-1">{String(parts.minutes).padStart(2, '0')}</span>
        <span className="text-white/40">:</span>
        <span className="rounded-md bg-cyan-400/18 px-2.5 py-1 text-cyan-100">{String(parts.seconds).padStart(2, '0')}</span>
      </div>
    );
  }

  // grid variant
  if (loading) {
    return (
      <div className="inline-flex items-center gap-1 rounded-xl border border-white/5 bg-black/20 px-2 py-1.5 text-[12px] font-black tabular-nums text-white/60">
        <span className="rounded-md bg-white/[0.03] px-2 py-1.5">0d</span>
        <span className="rounded-md bg-white/[0.03] px-2.5 py-1.5">00h 00min</span>
        <span className="rounded-md bg-white/[0.03] px-2 py-1.5">00s</span>
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 rounded-xl border border-white/5 bg-black/20 px-2 py-1.5 text-[12px] font-black tabular-nums text-white/60">
      <span className="rounded-md bg-white/[0.05] px-2 py-1.5">
        {parts.days}d
      </span>
      <span className="rounded-md bg-white/[0.05] px-2.5 py-1.5 text-white/90">
        {String(parts.hours).padStart(2, '0')}h {String(parts.minutes).padStart(2, '0')}min
      </span>
      <span className="rounded-md bg-cyan-400/12 px-2 py-1.5 text-cyan-100">
        {String(parts.seconds).padStart(2, '0')}s
      </span>
    </div>
  );
};

/**
 * Returns the label and subtitle for the countdown section based on campaign dates.
 * Use this in the parent to dynamically set the section header.
 */
export const getCountdownLabel = (
  startDate?: string | null,
  endDate?: string | null,
): { label: string; subtitle: string } => {
  const { mode } = getCountdownMode(startDate, endDate);

  if (mode === 'ended') {
    return { label: 'Campaign Ended', subtitle: 'The campaign period has concluded' };
  }
  if (mode === 'pre_start') {
    return { label: 'Starts In', subtitle: 'Countdown to campaign launch' };
  }
  return { label: 'Weekly Reset', subtitle: 'Mondays, 08:00 CET' };
};
